import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "antd";

import AUTH_SERVICES from "../../authService";
import { AUTH_ROUTES, resolveNextStep } from "../../authFlow";
import {
  clearSignupSession,
  patchSignupSession,
  readSignupSession,
} from "../../authSession";
import { parseAuthError } from "../../authErrors";
import { defaultOtpChannel, detectChannelType } from "../../authMappers";
import { OTP_LENGTH, OTP_TTL_SECONDS, RESEND_COOLDOWN_SECONDS } from "../../authPolicy";
import OtpInput from "../../components/OtpInput";
import AuthFooter, { LegalLink } from "../../components/AuthFooter";
import { showErrorToast } from "../../../../components/Toast";

import "../styles.css";

const FATAL_KINDS = ["SESSION_EXPIRED", "ACCOUNT_BLOCKED"];

const isFilled = (code) => code.length === OTP_LENGTH;

const formatClock = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return `${mins}:${String(secs).padStart(2, "0")}`;
};

const Verification = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  // Read once: this component is remounted per leg (see Route.js), so the
  // session it captures is always the current one.
  const session = useMemo(() => readSignupSession(), []);

  const stage = state?.stage || session?.stage || "CHANNEL_1";
  const mode = state?.mode || (session?.traceId ? "CONTACT" : "SESSION");
  const twoInputs = state?.twoInputs || stage === "BOTH";

  const [primary, setPrimary] = useState("");
  const [secondary, setSecondary] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [expiresIn, setExpiresIn] = useState(OTP_TTL_SECONDS);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [resendLocked, setResendLocked] = useState(false);

  const primaryRef = useRef(null);
  const secondaryRef = useRef(null);
  const submitting = useRef(false);

  /**
   * Resend depends entirely on where this leg came from, and getting it wrong
   * abandons the user's session. There is no session/resend/ endpoint.
   */
  const resendStrategy = useMemo(() => {
    if (mode === "CONTACT") return "OTP_REQUEST";
    if (stage === "CHANNEL_1" || stage === "BOTH") return "SESSION_START";

    // CHANNEL_2 reached from SignUp retains the details payload, so re-calling
    // details/ re-fires the code on the same session. Calling start/ here
    // would abandon a half-finished signup.
    if (session?.detailsPayload) return "SESSION_DETAILS";

    // CHANNEL_2 reached from an existing-user verify has no retained payload
    // and no resend route, so resend is genuinely impossible.
    return "NONE";
  }, [mode, stage, session]);

  useEffect(() => {
    if (!session) navigate(AUTH_ROUTES.getStarted, { replace: true });
  }, [session, navigate]);

  useEffect(() => {
    const tick = setInterval(() => {
      setExpiresIn((value) => (value > 0 ? value - 1 : 0));
      setCooldown((value) => (value > 0 ? value - 1 : 0));
    }, 1000);

    return () => clearInterval(tick);
  }, []);

  const resetToStart = useCallback(
    (message) => {
      showErrorToast(message, "Verification failed");
      clearSignupSession();
      navigate(AUTH_ROUTES.getStarted, { replace: true });
    },
    [navigate]
  );

  const submit = useCallback(async () => {
    if (submitting.current) return;

    if (expiresIn === 0) {
      setError("That code has expired. Tap resend to get a new one.");
      return;
    }

    submitting.current = true;
    setBusy(true);
    setError("");

    try {
      const response =
        mode === "CONTACT"
          ? await AUTH_SERVICES.otpVerify({
              traceId: session.traceId,
              otp: primary,
            })
          : await AUTH_SERVICES.sessionVerify({
              sessionId: session.sessionId,
              otp: primary,
              ...(twoInputs ? { otpSecondary: secondary } : {}),
            });

      // The standalone pair is a verification gate only — whatever it returns
      // is discarded, and signup continues to Add Photo.
      if (mode === "CONTACT") {
        navigate(AUTH_ROUTES.photo, { replace: true });
        return;
      }

      const decision = await resolveNextStep(response.data, { origin: "verify" });

      navigate(decision.route, {
        replace: true,
        state: {
          stage: decision.stage,
          twoInputs: decision.twoInputs,
          mode: decision.mode,
          legKey: decision.legKey,
        },
      });
    } catch (caught) {
      const authError = parseAuthError(caught);

      if (FATAL_KINDS.includes(authError.kind)) {
        resetToStart(authError.reason ?? authError.message);
        return;
      }

      // Verification renders its own copy inline — a raw server sentence
      // reads badly next to the boxes.
      setError(authError.message);

      if (authError.kind === "OTP_EXPIRED") setExpiresIn(0);
      if (authError.kind === "OTP_LIMIT") setResendLocked(true);

      // A network failure keeps the digits; a rejected code clears both
      // groups so the user is not editing a known-bad value.
      if (authError.kind !== "NETWORK") {
        setPrimary("");
        setSecondary("");
      }
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }, [
    expiresIn,
    mode,
    navigate,
    primary,
    resetToStart,
    secondary,
    session,
    twoInputs,
  ]);

  /**
   * The screen submits itself — there is no Verify button. In dual entry the
   * gate is both groups complete, so whichever the user finishes last fires
   * the request.
   */
  useEffect(() => {
    if (busy || !session) return;

    const ready = twoInputs
      ? isFilled(primary) && isFilled(secondary)
      : isFilled(primary);

    if (ready) submit();
  }, [primary, secondary, twoInputs, busy, session, submit]);

  /** Hand focus to the other group's first gap, not blindly to its box 1. */
  useEffect(() => {
    if (!twoInputs) return;

    if (isFilled(primary) && !isFilled(secondary)) {
      secondaryRef.current?.focusFirstEmpty();
    } else if (isFilled(secondary) && !isFilled(primary)) {
      primaryRef.current?.focusFirstEmpty();
    }
  }, [primary, secondary, twoInputs]);

  const onResend = async () => {
    if (resendLocked || cooldown > 0 || resendStrategy === "NONE") return;

    setError("");
    setBusy(true);

    try {
      if (resendStrategy === "OTP_REQUEST") {
        const channelType = detectChannelType(session.contact);

        const { data } = await AUTH_SERVICES.otpRequest({
          identityKey: session.detailsPayload?.secondChannelValue ?? session.contact,
          channel: defaultOtpChannel(channelType),
        });

        // A new traceId replaces the stored one.
        if (data?.traceId) patchSignupSession({ traceId: data.traceId });
      } else if (resendStrategy === "SESSION_DETAILS") {
        // Same sessionId; the second OTP fires again.
        const response = await AUTH_SERVICES.sessionDetails(session.detailsPayload);
        await resolveNextStep(response.data, { origin: "details" });
      } else {
        const channelType = detectChannelType(session.contact);

        const response = await AUTH_SERVICES.sessionStart({
          channelType,
          value: session.contact,
          otpChannel: defaultOtpChannel(channelType),
        });

        // Mints a NEW sessionId, which resolveNextStep adopts.
        await resolveNextStep(response.data, { origin: "start" });
      }

      setPrimary("");
      setSecondary("");
      setExpiresIn(OTP_TTL_SECONDS);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (caught) {
      const authError = parseAuthError(caught);

      if (FATAL_KINDS.includes(authError.kind)) {
        resetToStart(authError.reason ?? authError.message);
        return;
      }

      setError(authError.message);
      if (authError.kind === "OTP_LIMIT") setResendLocked(true);
    } finally {
      setBusy(false);
    }
  };

  if (!session) return null;

  const expired = expiresIn === 0;

  /**
   * Copy lifted from HexagieApp's Verification screen, including its split by
   * delivery channel — "SMS" and "OTP" are different words to a user, and the
   * app says whichever one it actually sent.
   */
  const channel = detectChannelType(session.contact);
  const primaryLabel = channel === "email" ? "email" : "phone number";
  const secondaryLabel = channel === "email" ? "phone number" : "email";

  const title = twoInputs
    ? "Verify both contacts"
    : channel === "phone"
    ? "We've sent you an SMS"
    : "We've sent you an OTP";

  const subtitle = twoInputs
    ? `Enter the codes sent to your ${primaryLabel} and ${secondaryLabel}`
    : `Enter the OTP sent to your ${primaryLabel}`;

  return (
    <div className="auth-page auth-page--alt auth-page--stacked">
      <div className="auth-screen auth-screen--stacked">
        <header className="auth-head">
          <h1>{title}</h1>
          <p className="auth-sub">{subtitle}</p>
        </header>

        <OtpInput
          ref={primaryRef}
          id="otp-primary"
          label={twoInputs ? `Code sent to your ${primaryLabel}` : undefined}
          value={primary}
          onChange={setPrimary}
          disabled={busy}
          autoFocus
          invalid={Boolean(error)}
        />

        {twoInputs && (
          <OtpInput
            ref={secondaryRef}
            id="otp-secondary"
            label={`Code sent to your ${secondaryLabel}`}
            value={secondary}
            onChange={setSecondary}
            disabled={busy}
            invalid={Boolean(error)}
          />
        )}

        <p className="auth-error" aria-live="polite">
          {error || (expired ? "That code has expired. Tap resend to get a new one." : "")}
        </p>

        <div className="auth-resend">
          <span className="auth-resend-status">
            {resendStrategy === "NONE" || resendLocked ? (
              <>
                <span className="auth-resend-label">Resend</span>
                <span className="auth-resend-off">Unavailable</span>
              </>
            ) : cooldown > 0 ? (
              <>
                <span className="auth-resend-label">Resend</span>
                <span className="auth-resend-timer">
                  {formatClock(cooldown)}sec
                </span>
              </>
            ) : (
              <button
                type="button"
                className="auth-resend-now"
                onClick={onResend}
                disabled={busy}
              >
                Resend now
              </button>
            )}
          </span>

          <button
            type="button"
            className="auth-resend-alt"
            onClick={() => resetToStart("Please start again.")}
          >
            Start over
          </button>
        </div>

        {busy && (
          <div className="auth-actions">
            <Button className="auth-btn-primary" size="large" block loading disabled>
              Verifying
            </Button>
          </div>
        )}

        <AuthFooter enterprise="By Sayagie Enterprise">
          By getting in, you accept our{" "}
          <LegalLink to="/terms">Terms of Use</LegalLink> and{" "}
          <LegalLink to="/privacy">Privacy Policy</LegalLink>
        </AuthFooter>
      </div>
    </div>
  );
};

export default Verification;
